import { runWithErrorReporting } from "$root/api/error-reporting";
import { getApiGen } from "$root/api/factory";
import { useApiGen } from "$root/api/hooks";
import MonitorWithHourglass from "$root/assets/images/glyphs[preload]/MonitorWithHourglass.svg";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import { IconWallBase, IconWalls } from "$root/components/IconWall";
import ReactQueryWrapper from "$root/components/ReactQueryWrapper";
import { currencyCode, formatCurrencyAmount } from "$root/utils/currency";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { useLocaleFormatters } from "$root/localization/hooks";
import { groupedColorProps } from "$root/pages/PortfolioDetails/PortfolioComposition/colors";
import {
	type PortfolioWidgetData,
	PortfolioQueryWidgetBase,
	WidgetStatus,
	portfolioWidgetMissingDataReason,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import { useMappedQuery, useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { commonGroupColumns } from "$root/ui-lib/common-table-columns";
import {
	type ContextContent,
	downloadContentDisposition,
	qualifier,
	stableColorGenerator,
	withContext,
} from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import {
	type InvestmentExposureEntry,
	InstrumentsClassificationsControllerV1ApiFactory,
	InvestmentExportControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import {
	type FieldSize,
	type Option,
	type TableWithGroupsProps,
	AutoSortTableWithGroups,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { Select, DropdownMenu, type DataAttributesProps } from "@mdotm/shadcn/mdotm/components";
import { type ClassList, overrideClassName, toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useQuery, type QueryKey } from "@tanstack/react-query";
import HighchartsReact from "highcharts-react-official";
import { Set } from "immutable";
import { type CSSProperties, type ReactNode, type StyleHTMLAttributes, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InvestmentExposureResponseExposureTypeEnum } from "./InvestmentExposureResponseExposureTypeEnum";
import { addMarketValue, hasMarketValue } from "./marketValue";

export type ExposureChartData = Array<{
	label: string;
	groupName: string;
	weight: number;
	netLong: boolean;
	marketValue?: number;
	drillDown: Array<{
		label: string;
		weight: number;
		netLong: boolean;
		marketValue?: number;
	}>;
}>;

export type ExposureCategory<
	TInvestmentExposureResponseExposureTypeEnum extends
		| InvestmentExposureResponseExposureTypeEnum
		| string = InvestmentExposureResponseExposureTypeEnum,
> = {
	label: string;
	columnTitle: string;
	type: "proxy" | "hedge" | "tag";
	value: TInvestmentExposureResponseExposureTypeEnum;
};

export const riskModelExposureCategories: Array<ExposureCategory<InvestmentExposureResponseExposureTypeEnum>> = [
	{
		label: "Macro Asset Class vs Micro Asset Class",
		columnTitle: "Macro Asset Class",
		type: "proxy",
		value: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
	},
	{
		label: "Macro Asset Class vs Geography",
		columnTitle: "Macro Asset Class",
		type: "proxy",
		value: "MACRO_ASSET_CLASS_VS_GEOGRAPHY",
	},
	{
		label: "Micro Asset Class vs Geography",
		columnTitle: "Micro Asset Class",
		type: "proxy",
		value: "MICRO_ASSET_CLASS_VS_GEOGRAPHY",
	},
	{
		label: "Geography vs Macro Asset Class",
		columnTitle: "Geography",
		type: "proxy",
		value: "GEOGRAPHY_VS_MACRO_ASSET_CLASS",
	},
	{
		label: "Geography vs Micro Asset Class",
		columnTitle: "Macro Geography",
		type: "proxy",
		value: "GEOGRAPHY_VS_MICRO_ASSET_CLASS",
	},
	{
		label: "Macro Asset Class vs Macro Geography",
		columnTitle: "Macro Asset Class",
		type: "proxy",
		value: "MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY",
	},
	{
		label: "Macro Asset Class vs Micro Geography",
		columnTitle: "Macro Asset Class",
		type: "proxy",
		value: "MACRO_ASSET_CLASS_VS_MICRO_GEOGRAPHY",
	},
	{
		label: "Micro Asset Class vs Macro Geography",
		columnTitle: "Micro Asset Class",
		type: "proxy",
		value: "MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY",
	},
	{
		label: "Micro Asset Class vs Micro Geography",
		columnTitle: "Micro Asset Class",
		type: "proxy",
		value: "MICRO_ASSET_CLASS_VS_MICRO_GEOGRAPHY",
	},
	{
		label: "Macro Geography vs Micro Geography",
		columnTitle: "Macro Geography",
		type: "proxy",
		value: "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
	},
	{
		label: "Currency",
		columnTitle: "Currency",
		type: "hedge",
		value: "CURRENCY",
	},
	{
		label: "Macro Asset Class",
		columnTitle: "Macro Asset Class",
		type: "proxy",
		value: "MACRO_ASSET_CLASS",
	},
	{
		label: "Macro Geography",
		columnTitle: "Macro Geography",
		type: "proxy",
		value: "MACRO_GEOGRAPHY",
	},
	{
		label: "Micro Geography",
		columnTitle: "Micro Geography",
		type: "proxy",
		value: "MICRO_GEOGRAPHY",
	},
	{
		label: "Micro Asset Class",
		columnTitle: "Micro Asset Class",
		type: "proxy",
		value: "MICRO_ASSET_CLASS",
	},
];

const allowedExposureCategoriesSet: Set<string> = Set([
	"MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
	"MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY",
	"MACRO_ASSET_CLASS_VS_MICRO_GEOGRAPHY",
	"MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY",
	"MICRO_ASSET_CLASS_VS_MICRO_GEOGRAPHY",
	"MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
	"CURRENCY",
] satisfies Array<InvestmentExposureResponseExposureTypeEnum>);

export type ExposureCategories = {
	fromRiskModel: Array<ExposureCategory>;
	fromTagClassifications: Array<ExposureCategory<string>>;
};

export async function fetchExposureCategories(allowedCategories: Set<string>): Promise<ExposureCategories> {
	const classifications = await axiosExtract(
		getApiGen(InstrumentsClassificationsControllerV1ApiFactory).retrieveAllClassifications(),
	);
	const tagClassifications = classifications.filter((c) => c.fieldType === "TAG");
	return {
		fromRiskModel: riskModelExposureCategories.filter((x) => allowedCategories.has(x.value)),
		fromTagClassifications: tagClassifications.map(
			(c) =>
				({
					label: c.name!,
					value: c.classificationUuid!,
					columnTitle: c.name!,
					type: "tag",
				}) satisfies ExposureCategory<string>,
		),
	};
}

type WithExposureCategoriesProps = {
	children(exposureCategories: ExposureCategories): ReactNode;
	loadingFallback?: ReactNode;
	onSuccess?: (data: ExposureCategories) => void;
} & (
	| { queryFn?: undefined; queryKey?: undefined }
	| { queryFn: () => Promise<ExposureCategories>; queryKey: QueryKey }
);

export function WithExposureCategories({
	children,
	loadingFallback = <IconWalls.CalculatingApi />,
	onSuccess,
	queryFn = () => fetchExposureCategories(allowedExposureCategoriesSet),
	queryKey = ["exposureOptions"],
}: WithExposureCategoriesProps): React.JSX.Element {
	return (
		<ReactQueryWrapper queryKey={queryKey} loadingFallback={loadingFallback} queryFn={queryFn} onSuccess={onSuccess}>
			{children}
		</ReactQueryWrapper>
	);
}

export function ExposureCategorySelector<
	TExposureRiskModelCategories extends
		InvestmentExposureResponseExposureTypeEnum = InvestmentExposureResponseExposureTypeEnum,
>({
	exposureCategory,
	setExposureCategory,
	exposureCategories,
	size,
	className,
	disabled,
	triggerDataAttrs,
}: {
	exposureCategory: ExposureCategoryState<TExposureRiskModelCategories>;
	setExposureCategory(category: ExposureCategoryState<TExposureRiskModelCategories>): void;
	exposureCategories: ExposureCategories;
	size?: FieldSize;
	className?: ClassList;
	/** Locks both levels — used while the displayed breakdown is stale w.r.t. the current composition. */
	disabled?: boolean;
	/** Data attributes for each level's Select trigger. */
	triggerDataAttrs?: { firstLevel?: DataAttributesProps; secondLevel?: DataAttributesProps };
}): React.JSX.Element {
	const secondLevelCategoriesOptions = useMemo(
		() =>
			exposureCategory.firstLevel === "RISK_MODEL"
				? exposureCategories.fromRiskModel.map((cat) => ({
						label: cat.label,
						value: cat.value,
						// group: "Risk model",
					}))
				: exposureCategories.fromTagClassifications
						.map((cat) => ({
							label: cat.label,
							value: cat.value,
							// group: "Tag",
						}))
						.sort(builtInSortFnFor("label")),
		[exposureCategory.firstLevel, exposureCategories.fromRiskModel, exposureCategories.fromTagClassifications],
	);

	const sanitizedFirstLevelOptions = useMemo(() => {
		const categoriesByFirstLevel = {
			RISK_MODEL: exposureCategories.fromRiskModel,
			CUSTOM_TAG: exposureCategories.fromTagClassifications,
		} satisfies Record<ExposureCategoryState["firstLevel"], ReadonlyArray<ExposureCategory<string>>>;

		return firstLevelOptions.filter((level) => categoriesByFirstLevel[level.value].length > 0);
	}, [exposureCategories.fromRiskModel, exposureCategories.fromTagClassifications]);

	return (
		<Row gap={8} className={toClassName(className)}>
			<Select
				size={size}
				disabled={disabled}
				value={exposureCategory.firstLevel}
				onChange={(selectedFirstLevel) => {
					if (selectedFirstLevel) {
						setExposureCategory(
							selectedFirstLevel === "CUSTOM_TAG"
								? {
										firstLevel: "CUSTOM_TAG",
										secondLevel: exposureCategories.fromTagClassifications.at(0)?.value ?? null,
									}
								: {
										firstLevel: "RISK_MODEL",
										secondLevel: (exposureCategories.fromRiskModel.at(0)?.value ??
											null) as TExposureRiskModelCategories | null,
									},
						);
					}
				}}
				options={sanitizedFirstLevelOptions}
				triggerDataAttrs={triggerDataAttrs?.firstLevel}
			/>
			<Select
				size={size}
				disabled={disabled}
				value={exposureCategory.secondLevel}
				enableSearch={secondLevelCategoriesOptions.length > 5}
				onChange={(selectedSecondLevel) => {
					if (selectedSecondLevel) {
						setExposureCategory(
							exposureCategory.firstLevel === "CUSTOM_TAG"
								? {
										firstLevel: "CUSTOM_TAG",
										secondLevel: selectedSecondLevel,
									}
								: {
										firstLevel: "RISK_MODEL",
										secondLevel: selectedSecondLevel as TExposureRiskModelCategories,
									},
						);
					}
				}}
				options={secondLevelCategoriesOptions}
				triggerDataAttrs={triggerDataAttrs?.secondLevel}
			/>
		</Row>
	);
}

export const ExposureChart = (props: {
	/** @default true */
	animated?: boolean;
	data: ExposureChartData;
	style?: StyleHTMLAttributes<HTMLDivElement>;
	customHeight?: CSSProperties["height"];
	hideLabel?: boolean;
	/** Renders the market value in the tooltip; the market value row is shown only when set. */
	formatMarketValue?: (value: number) => string;
}): React.JSX.Element => {
	const { formatNumber } = useLocaleFormatters();
	const { t } = useTranslation();
	const options = useMemo<Highcharts.Options>(
		() => ({
			chart: {
				type: "pie",
				style: { fontFamily: "Gotham,sans-serif" },
				reflow: true,
			},
			title: { text: undefined },
			subtitle: { text: undefined },
			panning: false,
			credits: { enabled: false },
			legend: { enabled: false },
			plotOptions: {
				pie: {
					animation: props.animated
						? undefined
						: {
								duration: 0,
							},
					shadow: false,
					size: "100%",
					dataLabels: {
						enabled: !props.hideLabel,
					},
				},
			},
			tooltip: {
				shared: true,
				useHTML: true,
				distance: 22,
				backgroundColor: "#FFFFFF",
				borderColor: undefined,
				borderRadius: 4,
				borderWidth: 0,
				style: {
					padding: "0",
					color: "#656d78",
					fontSize: "12px",
					boxShadow: "0 2px 10px 0 rgba(47, 53, 65, 0.15)",
				},
				formatter() {
					let tooltip_html =
						'<div class="DivTooltip" style="width: auto; min-width: 120px"><div class="Tooltip-title" style="color: #1D2433">' +
						this.key +
						((this.point as { netLong?: boolean }).netLong === false
							? ` - [
							<svg class="inline w-[8px] mb-1" width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle opacity="0.3" r="4" cx="4" cy="4" fill="${stableColorGenerator(
									(this.point as { groupName?: string }).groupName!,
								)}"></circle>
							</svg><span class="truncate pl-1">Net Short</span>]`
							: "") +
						"</div>";
					tooltip_html += "<table>";
					tooltip_html +=
						'<tr><td style="padding: 5px 8px 0 0" style="color: #2F3541">' +
						"Current" +
						'</td><td style="text-align: right; font-weight: 500;padding: 5px 0 0 0; color: #2F3541"> ' +
						formatNumber(this.y) +
						"%" +
						"</td></tr>";
					const marketValue = (this.point as { marketValue?: number }).marketValue;
					if (props.formatMarketValue !== undefined && marketValue !== undefined) {
						tooltip_html +=
							'<tr><td style="padding: 5px 8px 0 0" style="color: #2F3541">' +
							t("EXPOSURE.MONETARY_VALUE") +
							'</td><td style="text-align: right; font-weight: 500;padding: 5px 0 0 0; color: #2F3541"> ' +
							props.formatMarketValue(marketValue) +
							"</td></tr>";
					}
					tooltip_html += "</table></div>";
					return tooltip_html;
				},
			},
			series: [
				{
					name: "Primary",
					data: props.data.map((primary) => ({
						name: primary.label,
						groupName: primary.groupName,
						value: primary.weight,
						y: primary.weight,
						marketValue: primary.marketValue,
						color: stableColorGenerator(primary.groupName),
					})),
					dataSorting: {
						enabled: true,
						sortKey: "y",
					},
					size: "80%",
					type: "pie",
				},
				{
					name: "Secondary",
					states: {
						inactive: {
							enabled: false,
						},
					},
					data: props.data.flatMap((primary) =>
						primary.drillDown.length === 0
							? [
									{
										name: primary.label,
										value: primary.weight,
										y: primary.weight,
										color: "#ffffff",
										netLong: primary.netLong,
										marketValue: primary.marketValue,
										groupName: primary.groupName,
									},
								]
							: primary.drillDown.map((drillDown) => ({
									name: drillDown.label,
									value: primary.weight,
									y: drillDown.weight,
									color: Highcharts.color(stableColorGenerator(primary.groupName)).brighten(0.15).get(),
									netLong: drillDown.netLong,
									marketValue: drillDown.marketValue,
									groupName: primary.groupName,
								})),
					),
					type: "pie",
					size: "80%",
					innerSize: "70%",
					id: "secondary",
					dataLabels: { enabled: false },
				},
			],
			responsive: {
				rules: [
					{
						condition: {
							maxWidth: 420,
						},
						chartOptions: {
							pane: {
								size: "100%",
							},
						},
					},
				],
			},
			exporting: { enabled: false },
		}),
		[formatNumber, props.animated, props.data, props.hideLabel, props.formatMarketValue, t],
	);

	return (
		<HighchartsReact
			containerProps={{ style: { height: props.customHeight ?? "320px", width: "100%", ...props.style } }}
			highcharts={Highcharts}
			constructorType="chart"
			options={options}
		/>
	);
};

export function aggregateExposureData(composition: InvestmentExposureEntry[]): Record<
	string,
	{
		label: string;
		groupName: string;
		weight: number;
		netLong: boolean;
		marketValue?: number;
		drillDown: Array<{
			label: string;
			weight: number;
			netLong: boolean;
			marketValue?: number;
		}>;
	}
> {
	return composition.reduce(
		(acc, cur) => {
			if (!acc[cur.firstQualityLevel!]) {
				acc[cur.firstQualityLevel!] = {
					label: cur.firstQualityLevel!,
					weight: 0,
					drillDown: [],
					groupName: cur.firstQualityLevel!,
					netLong: cur.netLong ?? true,
				};
			}

			acc[cur.firstQualityLevel!].weight += cur.weight!;
			acc[cur.firstQualityLevel!].marketValue = addMarketValue(acc[cur.firstQualityLevel!].marketValue, cur.value);
			acc[cur.firstQualityLevel!].drillDown.push({
				label: cur.secondQualityLevel!,
				weight: cur.weight!,
				netLong: cur.netLong ?? true,
				marketValue: cur.value ?? undefined,
			});
			return acc;
		},
		{} as Record<string, ExposureChartData[number]>,
	);
}

export type ExposureCategoryState<
	TExposureRiskModelCategories extends
		InvestmentExposureResponseExposureTypeEnum = InvestmentExposureResponseExposureTypeEnum,
> =
	| {
			firstLevel: "CUSTOM_TAG";
			secondLevel: string | null;
	  }
	| {
			firstLevel: "RISK_MODEL";
			secondLevel: TExposureRiskModelCategories | null;
	  };

const firstLevelOptions = [
	{ label: "Risk Model", value: "RISK_MODEL" },
	{ label: "Tags", value: "CUSTOM_TAG" },
] as Option<ExposureCategoryState["firstLevel"]>[];

const ExposureEvolve = (ctx: ContextContent<typeof PortfolioContext>) => {
	const { portfolio, selectedBenchmark } = ctx;
	const { t } = useTranslation();

	const uuid = portfolio?.uuid;
	const benchmarkId = selectedBenchmark ?? portfolio?.primaryBenchmarkIdentifier;

	useWidgetOptions(
		() => ({
			title: t("EXPOSURE.TITLE"),
			tooltip: t("EXPOSURE.TOOLTIP"),
		}),
		[t],
	);

	const exposureAvailableQuery = useQueryNoRefetch(["queryExposureLevelEvolve", portfolio, uuid, benchmarkId], {
		enabled: Boolean(portfolio),
		queryFn: () => {
			if (!uuid || !benchmarkId) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(portfolio, "ExposureEvolve"),
				};
			}

			return {
				data: {},
				widgetStatus: WidgetStatus.READY,
			};
		},
	});

	return (
		<PortfolioQueryWidgetBase query={exposureAvailableQuery}>
			{() => (
				<WithExposureCategories>
					{(exposureCategories) => (
						<ExposureEvolveInner ctx={ctx} exposureCategories={exposureCategories} benchmarkId={benchmarkId} />
					)}
				</WithExposureCategories>
			)}
		</PortfolioQueryWidgetBase>
	);
};

/** The chart's exposure groups with each group's drill-down moved into the table's `rows`. */
function toExposureTableGroups(chartData: ExposureChartData) {
	return chartData.map((row) => ({
		...row,
		drillDown: undefined,
		rows: row.drillDown.map((x) => ({ ...x, groupName: row.groupName })),
	}));
}

type ExposureTableGroup = ReturnType<typeof toExposureTableGroups>[number];

/** The selected comparison as the two halves of the widget body read it. */
type ExposureSelectionData = {
	chartData: ExposureChartData;
	tableData: Array<ExposureTableGroup>;
};

/**
 * The wall for a comparison the exposure payload holds nothing for. The category selector stays above
 * it — this is the wall for one selection, not for the widget — so it names the way out.
 */
function ExposureSelectionEmptyWall(): React.JSX.Element {
	return (
		<div className="w-full grow min-h-0 pt-2">
			<IconWallBase>
				<div className="flex-1 min-h-0 flex flex-col">
					<img alt="illustration" src={MonitorWithHourglass} className="mx-auto my-0" />
					<Text as="div" className="px-1 text-center" type="Body/M/Book">
						Data not available for the selected comparison, please pick another option.
					</Text>
				</div>
			</IconWallBase>
		</div>
	);
}

const ExposureEvolveInner = (props: {
	ctx: ContextContent<typeof PortfolioContext>;
	exposureCategories: ExposureCategories;
	benchmarkId: string | undefined;
}) => {
	const [exposureCategory, setExposureCategory] = useState<ExposureCategoryState>({
		firstLevel: "RISK_MODEL",
		secondLevel: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS" satisfies InvestmentExposureResponseExposureTypeEnum,
	});

	const { t } = useTranslation();
	const { formatNumber } = useLocaleFormatters();
	const { ctx, exposureCategories, benchmarkId } = props;
	const { portfolio, enhanced } = ctx;

	const query = useQuery({
		...portfolioRepository.exposure.getQueryConfig(portfolio?.uuid ?? "", {
			benchmarkId: benchmarkId ?? "",
			secondLevel: exposureCategory.secondLevel ?? "",
			enhanced,
			asOf: ctx.asOf,
		}),
		keepPreviousData: true,
		enabled: isAsOfReadable(ctx.asOf, ctx.asOfSnapshot),
	});

	const exportApi = useApiGen(InvestmentExportControllerApiFactory);

	const exportPortfolioExposureNew = async (uuid: string) => {
		await runWithErrorReporting(
			async () => {
				const response = await exportApi.exportMultiGraphOfExposure(uuid, ctx.asOf, { responseType: "blob" });
				await downloadContentDisposition(response);
			},
			{ area: "portfolio", attemptedOperation: { message: "try to export exposure" } },
		);
	};

	const exportPortfolioExposureNewRef = useUnsafeUpdatedRef(exportPortfolioExposureNew);

	useWidgetOptions(
		() => ({
			title: (
				<Text
					type="Body/XL/Bold"
					title={t("EXPOSURE.TITLE")}
					className="truncate"
					data-qualifier={qualifier.widgets.portfolioExposure.name}
				>
					{t("EXPOSURE.TITLE")}
				</Text>
			),
			isLoading: query.isFetching,
			tooltip: t("EXPOSURE.TOOLTIP"),
			actionHeader: !(query.isError || query.isFetching) ? (
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					extraTriggerProps={{ "data-qualifier": qualifier.component.dropdown.trigger("Exposure/Download") }}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await exportPortfolioExposureNewRef.current(portfolio.uuid);
							},
							label: `${t("EXPOSURE.DOWNLOAD_TITLE")}`,
							"data-qualifier": qualifier.component.dropdown.item("Exposure/Composition"),
						},
					]}
				/>
			) : null,
		}),
		[t, query.isError, query.isFetching, exportPortfolioExposureNewRef, portfolio],
	);

	// The payload carries a whole comparison, so whether the selection has anything to show is known
	// here rather than in the render below.
	const exposureQuery = useMappedQuery(
		(data): PortfolioWidgetData<ExposureSelectionData | undefined> => {
			const composition = enhanced ? data?.enhancementComposition : data?.investmentComposition;
			const chartData = composition ? Object.values(aggregateExposureData(composition)) : [];
			if (chartData.length === 0) {
				return {
					data: undefined,
					widgetStatus: WidgetStatus.EMPTY,
				};
			}

			return {
				data: { chartData, tableData: toExposureTableGroups(chartData) },
				widgetStatus: WidgetStatus.READY,
			};
		},
		[enhanced],
		query,
	);

	const marketValueCurrency = useMemo(() => {
		const composition = enhanced ? query.data?.enhancementComposition : query.data?.investmentComposition;
		return hasMarketValue(exposureCategory.firstLevel, composition) ? currencyCode(portfolio.baseCurrency) : undefined;
	}, [enhanced, query.data, exposureCategory.firstLevel, portfolio.baseCurrency]);

	const formatMarketValue = useCallback(
		(value: number | undefined) => (value === undefined ? null : formatNumber(value, 2)),
		[formatNumber],
	);

	const formatTooltipMarketValue = useMemo(
		() =>
			marketValueCurrency === undefined
				? undefined
				: (value: number) => formatCurrencyAmount(value, portfolio.baseCurrency),
		[marketValueCurrency, portfolio.baseCurrency],
	);

	const columns = useCallback<TableWithGroupsProps<ExposureTableGroup>["columns"]>(
		({ bandColumn, expandColumn }) => [
			bandColumn,
			expandColumn,
			{
				header: (props.exposureCategories.fromRiskModel.find((cat) => cat.value === exposureCategory.secondLevel) ??
					props.exposureCategories.fromTagClassifications.find((cat) => cat.value === exposureCategory.secondLevel))!
					.columnTitle,
				groupContent: (groupedRow) => groupedRow.label,
				content: (row, cellProps) => (
					<Row {...cellProps} alignItems="center">
						<span className="line-clamp-2">
							{row.label}{" "}
							{!row.netLong && (
								<>
									-{" "}
									<span className="whitespace-nowrap">
										[{" "}
										<svg
											className="inline w-[8px] mb-1"
											width="8"
											height="8"
											viewBox="0 0 8 8"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<rect opacity="0.3" width="8" height="8" rx="4" fill={stableColorGenerator(row.groupName)} />
										</svg>{" "}
										<span className="truncate">Net Short</span> ]
									</span>
								</>
							)}
						</span>
					</Row>
				),
				sortFn: builtInSortFnFor("label"),
				name: "label",
			},
			{
				...commonGroupColumns.percentage("weight", "weight"),
				width: 120,
				header: "Weight",
			},
			...(marketValueCurrency === undefined
				? []
				: [
						{
							name: "marketValue",
							header: `${t("EXPOSURE.MONETARY_VALUE")} (${marketValueCurrency})`,
							width: 140,
							align: "end" as const,
							cellClassName: "tabular-nums",
							groupCellClassName: "tabular-nums",
							groupContent: (group: ExposureTableGroup) => formatMarketValue(group.marketValue),
							content: (row: ExposureTableGroup["rows"][number]) => formatMarketValue(row.marketValue),
							sortFn: builtInSortFnFor<ExposureTableGroup>("marketValue"),
						},
					]),
		],
		[
			exposureCategory,
			props.exposureCategories.fromRiskModel,
			props.exposureCategories.fromTagClassifications,
			marketValueCurrency,
			formatMarketValue,
			t,
		],
	);
	return (
		<div className="flex flex-col min-h-0 grow">
			<ExposureCategorySelector
				className="max-w-2/4"
				exposureCategory={exposureCategory}
				setExposureCategory={setExposureCategory}
				exposureCategories={exposureCategories}
			/>
			<PortfolioQueryWidgetBase query={exposureQuery} iconWalls={{ empty: <ExposureSelectionEmptyWall /> }}>
				{({ chartData, tableData }) => (
					<div className="flex grow min-h-0">
						<div className="min-w-0 w-1/2 h-full">
							<div className={toClassName({ "opacity-60": query.isFetching })}>
								<ExposureChart animated data={chartData} formatMarketValue={formatTooltipMarketValue} />
							</div>
						</div>
						<div
							className={overrideClassName("w-1/2 flex flex-col min-h-0", {
								"opacity-60": query.isFetching,
							})}
						>
							<AutoSortTableWithGroups
								groupedRows={tableData}
								headerRowClassName="sticky top-0 z-10 bg-white"
								groupRowKey={(x) => x.groupName}
								{...groupedColorProps("groupName", "groupName")}
								columns={columns}
							/>
						</div>
					</div>
				)}
			</PortfolioQueryWidgetBase>
		</div>
	);
};

export default withContext(PortfolioContext)(ExposureEvolve);
